// @ts-check
// The server-session adapter (PLATFORM-PLAN-9). Implements the TokenSource
// shape from tokenSource.js.
//
// THERE IS NO TOKEN IN THIS TAB, AND THAT IS THE POINT. Sign-in is a top-level
// navigation through the Academy's own server, which redeems the code with a
// client secret and keeps the access, refresh and id tokens in Redis. The
// browser holds one HttpOnly cookie script cannot read; an XSS here can make
// requests while the page is open, but it cannot carry a token away and use it
// tomorrow.
//
// Renewal is the server's job too: it refreshes the access token when a request
// arrives near expiry, so there is no refresh grant, no single-flight latch and
// no invalid_grant handling left in the client.

import { apiOrigin } from '../config/environment';

/**
 * @typedef {import('./tokenSource').Actor} Actor
 */

// The origin, not apiBaseUrl (which already ends in /api/academy). Empty in
// every real deployment: the session cookie is host-only, so the SPA and the
// server it signs in through must share an origin.
const BFF = `${apiOrigin}/api/bff`;

/**
 * Sent on every call. The server only lets the session cookie stand in for a
 * bearer token when this header is present — a cross-origin page cannot add
 * it without a CORS preflight, and the only origins the Academy's CORS policy
 * admits are its own frontends. See Academy.Server/Auth/BffCsrf.cs.
 */
export const CSRF_HEADER = 'X-CSRF';

/**
 * Where a signed-in user belongs when the identity provider hands them back
 * from nowhere in particular.
 *
 * `/dashboard` is the app home; `/` is the public marketing page, so it is
 * never a post-sign-in destination even when it is where the visitor pressed
 * "Sign in" (the landing header and footer both carry the button).
 */
export const POST_SIGN_IN_PATH = '/dashboard';

/**
 * The path to come back to after signing in.
 *
 * Only a same-origin ABSOLUTE PATH is accepted — the server re-checks it
 * (BffExtensions.LocalPathOrRoot), because a `returnUrl` is a query parameter
 * anybody can write into a link, and a value starting `//` or carrying a scheme
 * would be an open redirect. And the public page is a valid place to BE, but
 * never a valid place to be sent after signing in — that is the "broken
 * session" landing on the sales pitch that this exists to prevent.
 *
 * @param {unknown} path
 * @returns {string}
 */
export function returnPath(path) {
    if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')) {
        return POST_SIGN_IN_PATH;
    }
    // `/?x` and `/#y` are still the marketing page.
    return /^\/(?:[?#]|$)/.test(path) ? POST_SIGN_IN_PATH : path;
}

// One silent attempt per tab per two minutes. The server keeps its own marker
// cookie for the same window; this one is what stops the PAGE asking again
// after Keycloak said login_required and sent the browser back.
//
// sessionStorage holds a TIMESTAMP here and nothing else — never a credential.
const SILENT_KEY = 'bff.silentAttemptAt';
const SILENT_WINDOW_MS = 2 * 60 * 1000;

// Between asking for a navigation and the document actually leaving, more
// components keep rendering. The first redirect wins; the latch is cleared when
// the back/forward cache restores this document (pageshow.persisted), because
// module state survives that restore exactly as it was.
let redirecting = false;
if (typeof window !== 'undefined') {
    window.addEventListener('pageshow', (event) => {
        if (event.persisted) redirecting = false;
    });
}

/** @param {string} url */
function navigate(url) {
    redirecting = true;
    window.location.assign(url);
}

function currentPath() {
    if (typeof window === 'undefined') return POST_SIGN_IN_PATH;
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

/** @param {string} path */
function loginUrl(path, silent = false) {
    const returnUrl = encodeURIComponent(returnPath(path));
    return `${BFF}/login?returnUrl=${returnUrl}${silent ? '&prompt=none' : ''}`;
}

function readSilentAt() {
    try { return Number(window.sessionStorage.getItem(SILENT_KEY)) || 0; } catch { return Date.now(); }
}

function writeSilentAt(/** @type {number|null} */ value) {
    try {
        if (value == null) window.sessionStorage.removeItem(SILENT_KEY);
        else window.sessionStorage.setItem(SILENT_KEY, String(value));
    } catch { /* storage blocked: canAttemptSilentSignIn already answered false */ }
}

/**
 * Coming back from Keycloak's end-session endpoint (?signedOut=1, on the public
 * page): do not try a silent sign-in straight away, which would only bounce off
 * Keycloak. The flag is stripped from the address bar so a refresh behaves
 * normally.
 */
function consumeSignedOutFlag() {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('signedOut') !== '1') return;
    url.searchParams.delete('signedOut');
    window.history.replaceState(window.history.state, document.title, `${url.pathname}${url.search}${url.hash}`);
    writeSilentAt(Date.now());
}

consumeSignedOutFlag();

/**
 * WHAT THIS PERSON IS TO THE ACADEMY — GET /my/access (A4). Whether they are a
 * learner and which companies or universities they manage: records the Academy
 * holds, not roles, so they cannot arrive in the token.
 *
 * A hint for the UI and nothing more; the server enforces every call. Any
 * failure — an unlinked identity (403 actor_unmatched), a network blip — is
 * "no access information", never a failed sign-in: the person is still signed in
 * and everything their roles grant still works.
 *
 * (Not services/myService.getMyAccess: that goes through ../api, which imports
 * this module for CSRF_HEADER, and a cycle here would be a module that half
 * loads.)
 *
 * @returns {Promise<{ isLearner: boolean, managerOf: { organisationId: string, name: string }[] }|null>}
 */
async function getAccess() {
    try {
        const response = await fetch(`${apiOrigin}/api/academy/my/access`, {
            credentials: 'same-origin',
            cache: 'no-store',
            headers: { Accept: 'application/json', [CSRF_HEADER]: '1' },
        });
        if (!response.ok) return null;

        const body = await response.json();
        return {
            isLearner: body?.isLearner === true,
            managerOf: Array.isArray(body?.managerOf)
                ? body.managerOf.filter((o) => o && typeof o.organisationId === 'string')
                    .map((o) => ({ organisationId: o.organisationId, name: String(o.name ?? '') }))
                : [],
        };
    } catch {
        return null;
    }
}

/** @returns {Promise<Actor|null>} */
async function getActor() {
    const response = await fetch(`${BFF}/user`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json', [CSRF_HEADER]: '1' },
    });

    // No session is not an error: RequireAuth decides between a silent attempt
    // and the gate, and the public pages carry on without one. Anything else
    // is — a 500 here must not read as "signed out".
    if (response.status === 401) return null;
    if (!response.ok) throw new Error(`[auth] ${BFF}/user answered ${response.status}`);

    const user = await response.json();
    writeSilentAt(null);

    // WHAT THIS PERSON IS TO THE ACADEMY, for the organisation picker and the
    // learner hint only. A manager's ACCESS to the company portal is the
    // academy.company screen permission below, not this record; managerOf just
    // says which organisations they may choose between.
    const access = await getAccess();
    const managerOf = access?.managerOf ?? [];

    return {
        // Keycloak's sub: the realm-internal user UUID.
        id: user.sub ?? '',
        displayName: user.name ?? user.preferredUsername ?? '',
        email: user.email ?? undefined,
        // Keycloak roles from the access token the SERVER validated. They decide
        // nothing any more: access is the screen permissions below.
        roles: Array.isArray(user.roles) ? user.roles : [],
        // The screen-permission patterns Nexus granted ("academy.my.view",
        // "academy.*", ...). ui-kit's canScreen matches against these. An empty
        // array is a real state: signed in, nothing granted yet.
        permissions: Array.isArray(user.permissions) ? user.permissions : [],
        managerOf,
        isLearner: access?.isLearner ?? false,
        // Whether the server can reach Nexus to send a password reset email
        // (Binaa.Platform.Auth 0.8.0). false hides the kit ProfileMenu's
        // "Email me a password reset link" item; absent means yes.
        passwordReset: user.passwordReset !== false,
    };
}

/**
 * Whether RequireAuth should hold a spinner and try prompt=none rather than
 * render the sign-in gate. True while a redirect is already under way.
 */
export function canAttemptSilentSignIn() {
    if (redirecting) return true;
    if (typeof window === 'undefined') return false;
    return Date.now() - readSilentAt() > SILENT_WINDOW_MS;
}

/**
 * One prompt=none round trip through Keycloak. A person already signed in to
 * any platform application comes straight back signed in here; anyone else
 * comes back to the same page, where the gate then renders.
 *
 * ONLY EVER CALLED FROM RequireAuth, which no public route is wrapped in: the
 * landing page, the catalogue, the checkout and the verifier must never bounce
 * an anonymous visitor through the identity provider.
 */
export function attemptSilentSignIn() {
    if (redirecting || !canAttemptSilentSignIn()) return;
    writeSilentAt(Date.now());
    navigate(loginUrl(currentPath(), true));
}

async function signIn() {
    if (redirecting) return;
    navigate(loginUrl(currentPath()));
}

async function signOut() {
    if (redirecting) return;

    // LATCHED BEFORE THE REQUEST, not after it. The shell clears the actor the
    // moment sign-out starts, so the protected screen underneath re-renders as
    // signed out while the logout call is still in flight — and RequireAuth,
    // seeing no actor and no spent attempt, would start a prompt=none round
    // trip that the still-live Keycloak session answers by signing the person
    // straight back in. The latch makes that attempt a no-op, and the marker
    // keeps it one after the page comes back.
    redirecting = true;
    writeSilentAt(Date.now());

    /** @type {string} */
    let destination = '/?signedOut=1';
    try {
        const response = await fetch(`${BFF}/logout`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { Accept: 'application/json', [CSRF_HEADER]: '1' },
        });
        if (response.ok) destination = (await response.json()).url ?? destination;
    } catch {
        // The server session may have survived; the gate after the reload says
        // so honestly instead of pretending the sign-out worked.
    }
    // Ends the Keycloak SSO session too, and lands on the public page (`/`).
    // Otherwise the next silent attempt signs the person straight back in,
    // which looks like sign-out not working.
    navigate(destination);
}

/** @type {import('./tokenSource').TokenSource} */
export const bffTokenSource = {
    // Always null: requests authenticate with the session cookie, and the
    // server attaches the token itself.
    getAccessToken: async () => null,
    getActor,
    signIn,
    signOut,
    interactive: true,
};
